import React, { useRef } from 'react';
import styles from './otp-input.module.scss';
import { autoId } from '../../utils/AutomationUtil';
import classNames from 'classnames';

interface OTPInputProps {
    disabled?: boolean;
    value: string;
    onChange: (value: string) => void;
    className?: string;
    autoFocus?: boolean;
    ariaDescribedBy?: string;
    labels: string[];
}

type FocusDirection = 'next' | 'previous';

const digits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;

type Digit = typeof digits[number];
type DigitOrBlank = Digit | '';

function isDigit(digitStr): digitStr is Digit {
    return !!digits.find(e => e === digitStr);
}

const keyCodes = ['Backspace', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Space'];

type KeyCode = typeof keyCodes[number];

function isKeyCode(keyCode): keyCode is KeyCode {
    return !!keyCodes.find(e => e === keyCode);
}

const focusToInput = (target: HTMLElement, focusDirection: FocusDirection) => {
    const siblings: Record<FocusDirection, Element | null> = {
        next: target.nextElementSibling,
        previous: target.previousElementSibling
    };

    const elementSibling = siblings[focusDirection] as HTMLInputElement | null;

    if (elementSibling) {
        elementSibling.focus();
    }
};

function createValueRef(value: string, otpLength): Array<DigitOrBlank> {
    const arr: Array<DigitOrBlank> = [];
    for (let i = 0; i < otpLength; i++) {
        const char = value[i];
        if (isDigit(char)) {
            arr.push(char);
        } else {
            arr.push('');
        }
    }
    return arr;
}

function createStringValue(values: Array<DigitOrBlank>) {
    return values.reduce<string>((acc, val) => acc + val, '');
}

const otpLength = 6;
const inputMaxLength = 1;

export const OTPInput = ({
    disabled,
    className,
    value,
    onChange,
    autoFocus,
    ariaDescribedBy,
    labels
}: OTPInputProps) => {
    const classes = classNames(styles.otpInput, className);

    const inputRefs = useRef(new Array(otpLength));

    // valueRef tracks the value of the otp, but as an array.
    // elements of the array can be blank
    const valueRef = useRef<Array<DigitOrBlank>>(createValueRef(value, inputRefs.current.length));

    const inputOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index) => {
        const target = e.target as HTMLInputElement;
        const targetValue = target.value;

        const keyCode = isKeyCode(e.code) ? e.code : e?.key ? e.key : null;

        const keyCodeTests: Array<{ match: boolean; preventDefault?: boolean; focusDirection?: FocusDirection }> = [
            {
                match: keyCode === 'Space' || keyCode === 'ArrowUp' || keyCode === 'ArrowDown',
                preventDefault: true
            },
            {
                match: keyCode === 'ArrowRight' && index === otpLength - 1,
                preventDefault: true
            },
            {
                match: keyCode === 'ArrowLeft' && index === 0,
                preventDefault: true
            },
            {
                match: keyCode === 'Backspace' && targetValue === '',
                focusDirection: 'previous'
            },
            {
                match: keyCode === 'ArrowRight',
                focusDirection: 'next'
            },
            {
                match: keyCode === 'ArrowLeft',
                focusDirection: 'previous'
            },
            {
                match: targetValue === e.key,
                preventDefault: true,
                focusDirection: 'next'
            }
        ];

        const found = keyCodeTests.find(test => test.match);

        if (found?.preventDefault) {
            e.preventDefault();
        }

        if (found?.focusDirection) {
            focusToInput(target, found.focusDirection);
        }
    };

    const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const target = e.target;
        const targetValue = target.value.trim();
        const isTargetValueDigit = isDigit(targetValue);
        const isBlank = targetValue === '';

        if (targetValue.length > 1) {
            e.preventDefault();
            spreadDigits(targetValue, index);
        }

        if (!isTargetValueDigit && !isBlank) {
            return;
        }

        valueRef.current[index] = targetValue;

        if (isTargetValueDigit) {
            focusToInput(target, 'next');
        }

        const stringValue = createStringValue(valueRef.current);
        onChange(stringValue);
    };

    const spreadDigits = (pasteValue: string, index: number) => {
        const valueLength = valueRef.current.length - index;

        if (pasteValue.match(/^\d+$/)) {
            let lastRef;
            for (let i = 0; i < valueLength && i < pasteValue.length; i++) {
                const char = pasteValue[i];
                if (isDigit(char)) {
                    valueRef.current[index + i] = char;
                }
                lastRef = inputRefs.current[index + i];
            }
            lastRef.focus();

            const stringValue = createStringValue(valueRef.current);
            onChange(stringValue);
        }
    };

    function onFocus(e: React.FocusEvent<HTMLInputElement>) {
        // setTimeout is for cross-browser compatability
        // https://stackoverflow.com/questions/12047648/setselectionrange-with-on-click-in-chrome-does-not-select-on-second-click
        setTimeout(function () {
            e.target.setSelectionRange(0, inputMaxLength);
        }, 0);
    }

    function onClick(e: React.MouseEvent<HTMLInputElement>) {
        const target = e.target as HTMLInputElement;
        setTimeout(function () {
            target.setSelectionRange(0, inputMaxLength);
        });
    }

    function onPaste(e, index) {
        e.preventDefault();
        const pasteValue = e.clipboardData.getData('Text');

        spreadDigits(pasteValue, index);
    }

    return (
        <div className={classes}>
            {valueRef.current.map((digit, index) => (
                <input
                    aria-describedby={ariaDescribedBy}
                    autoFocus={index === 0 ? autoFocus : undefined}
                    ref={ref => {
                        inputRefs.current[index] = ref;
                    }}
                    {...autoId(`OTPVerification__digit${index}`)}
                    onClick={onClick}
                    onChange={e => inputOnChange(e, index)}
                    onKeyDown={e => inputOnKeyDown(e, index)}
                    onFocus={onFocus}
                    onPaste={e => onPaste(e, index)}
                    key={index}
                    type='text'
                    value={digit}
                    autoComplete='one-time-code'
                    inputMode='numeric'
                    className={styles.digit}
                    disabled={disabled}
                    aria-label={labels[index]}
                    maxLength={otpLength}
                    pattern='\d{1}'
                />
            ))}
        </div>
    );
};
